import { MapPin, Pencil } from "lucide-react";
import { Link } from "react-router";
import { countryLabel, progressToHandover, type Project } from "@gb/contracts";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AddDocumentsBanner, AddDocumentsToBrain } from "./brain-signpost";

/** Today as a bare ISO day, in the viewer's own timezone. */
function todayIso(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function ProjectOverview({
  project,
  roleProfile,
  documentCount,
}: {
  project: Project;
  /** role label → doc labels, from the country's jurisdiction. Empty when
   *  the country has no configured profile. */
  roleProfile?: { role: string; docs: string[] }[];
  /** How many documents are filed against this project. Undefined while the
   *  list is still loading, or if it failed — the signpost stands either way. */
  documentCount?: number;
}) {
  const percent = progressToHandover(project.startDate, project.targetHandoverDate, todayIso());

  return (
    <div className="flex flex-col gap-[18px]">
      <AddDocumentsBanner projectId={project.id} />

      <header className="rounded-card-lg border-[1.5px] border-border bg-surface px-7 py-6 shadow-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-ok/40 bg-ok-bg px-[11px] py-1 font-display text-[0.74rem] font-semibold uppercase tracking-[0.03em] text-ok">
                <span aria-hidden className="size-[9px] rounded-full bg-ok" />
                Active
              </span>
              {project.region && (
                <span className="flex items-center gap-1 text-[0.84rem] font-semibold text-muted">
                  <MapPin size={14} aria-hidden />
                  {project.region}
                </span>
              )}
            </div>
            <h2 className="font-display text-[1.7rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
              {project.name}
            </h2>
            {project.spec && (
              <p className="mt-1 text-[0.95rem] text-muted">{project.spec}</p>
            )}
          </div>
          {/* A `Link`, not a `button` — it navigates. It used to point at
              `/project/setup/1`, which matched no route: the singular prefix
              was left behind when routing moved to `/projects/:projectId`, so
              the control fell through the catch-all and dropped the user on
              the picker. Step 1 rather than bare `setup`, because `setup`
              redirects to `project.setupStep` — the last step on every live project — and
              would open Review instead of the flow. */}
          <Button variant="secondary" size="sm" asChild>
            <Link to={`/projects/${project.id}/setup/1`}>
              <Pencil size={14} />
              Edit project
            </Link>
          </Button>
        </div>

        {percent !== null && project.startDate && project.targetHandoverDate && (
          <div className="mt-6">
            <div className="mb-2 flex items-baseline justify-between gap-3 text-[0.8rem] font-semibold text-muted">
              <span>{formatDate(project.startDate)}</span>
              <span>{formatDate(project.targetHandoverDate)}</span>
            </div>
            <div
              className="h-2.5 w-full overflow-hidden rounded-full bg-subtle"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Progress to target handover"
            >
              <div className="h-full rounded-full bg-brand" style={{ width: `${percent}%` }} />
            </div>
            <p className="mt-2 text-[0.84rem] font-semibold text-ink-slate">
              ~{percent}% to target handover
            </p>
          </div>
        )}
      </header>

      <div className="grid gap-[18px] md:grid-cols-2">
        <section className="rounded-card-lg border-[1.5px] border-border bg-surface px-6 py-[22px] shadow-card">
          <SectionLabel className="mb-4">Project basics</SectionLabel>
          <dl className="flex flex-col">
            <Row label="Country" value={countryLabel(project.country)} />
            <Row label="Region" value={project.region} />
            <Row label="Capacity / spec" value={project.spec} />
            <Row
              label="Started"
              value={project.startDate ? formatDate(project.startDate) : null}
            />
            <Row
              label="Target handover"
              value={
                project.targetHandoverDate ? formatDate(project.targetHandoverDate) : null
              }
              last
            />
          </dl>
        </section>

        <section className="rounded-card-lg border-[1.5px] border-border bg-surface px-6 py-[22px] shadow-card">
          <SectionLabel className="mb-4">Crew compliance profile</SectionLabel>
          {roleProfile && roleProfile.length > 0 ? (
            <>
              <ul className="flex flex-col gap-3">
                {roleProfile.map((entry) => (
                  <li key={entry.role}>
                    <div className="mb-1.5 text-[0.84rem] font-semibold text-ink">
                      {entry.role}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {entry.docs.map((doc) => (
                        <span
                          key={doc}
                          className="rounded-full border-[1.5px] border-border bg-subtle px-2.5 py-[3px] text-[0.75rem] font-semibold text-ink-slate"
                        >
                          {doc}
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[0.8rem] leading-[1.5] text-muted">
                Required documents per role — drives every compliance flag (
                {countryLabel(project.country)} jurisdiction).
              </p>
            </>
          ) : (
            <p className="text-[0.86rem] leading-[1.5] text-muted">
              No compliance profile is configured for{" "}
              {countryLabel(project.country)} yet.
            </p>
          )}
        </section>
      </div>

      {/* A signpost, not a list. Documents are added in exactly one place —
          the Brain — so this page carries no dropzone, no file input and no
          connector buttons. The count is here because a signpost that cannot
          say whether there is anything to go and look at is doing half a job. */}
      <section className="rounded-card-lg border-[1.5px] border-border bg-surface px-6 py-[22px] shadow-card">
        <SectionLabel
          className="mb-3"
          trailing={
            documentCount === undefined
              ? undefined
              : `${documentCount} ${documentCount === 1 ? "document" : "documents"}`
          }
        >
          Documents
        </SectionLabel>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-[0.88rem] leading-[1.5] text-ink-prose">
            Documents live in the Brain.
          </p>
          <AddDocumentsToBrain projectId={project.id} />
        </div>
      </section>
    </div>
  );
}

function Row({
  label,
  value,
  last,
}: {
  label: string;
  value: string | null | undefined;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4 py-[9px]",
        !last && "border-b border-border-inner",
      )}
    >
      <dt className="text-[0.84rem] font-semibold text-muted">{label}</dt>
      <dd
        className={cn(
          "text-right text-[0.88rem]",
          value ? "font-semibold text-ink" : "text-faint",
        )}
      >
        {value || "Not set"}
      </dd>
    </div>
  );
}
